import React, { useState, useEffect, useMemo, useCallback, createContext, useContext } from 'react';
import PropTypes from 'prop-types';
import { get, isEmpty, noop } from 'lodash-es';
import { venomHistory } from 'client/utils/history/venom-history';
import { getAppraisalLink } from 'site-modules/shared/utils/appraisal-link-constructor';
import { checkVinInventory } from 'site-modules/shared/utils/appraisal/check-vin-inventory';
import { APPRAISAL_VEHICLE_ENTRY_CREATIVE_ID } from 'client/site-modules/shared/constants/appraisal/appraisal';
import { TrackingConstant } from 'client/tracking/constant';
import { usePrevious } from 'site-modules/shared/hooks/use-previous';
import { getQuery } from 'client/utils/location';
import { EventToolbox } from 'client/utils/event-toolbox';
import { getColorIdForUrl } from 'site-modules/shared/components/appraisal/appraisal-tabs-v2/utils/get-color-id-for-url';

/**
 * Appraisal tabs context contract:
 * - The provider owns all local state and redirect side effects.
 * - Config context holds stable props such as flags, callbacks, and location;
 *   state context holds volatile form state that changes on user interaction.
 *   Keep consumers on the narrow hook they need so pure-config consumers do not
 *   re-render on every form keystroke.
 * - Only `vin` is passed through to WrappedComponent because connectToModel's
 *   bindToPath reads it from props to build model paths. Other provider values,
 *   including setters, should be read from the context hooks.
 * - `isOpenInNewTab` is provider-internal because only the redirect effect uses
 *   it. `setColors` is exposed without a `colors` getter because colors are only
 *   consumed internally by the redirect effect for URL color IDs.
 * - Prefer `useAppraisalTabsConfig()` for flags/config, `useAppraisalTabsState()`
 *   for form state, and both hooks separately when a component needs both.
 */

// --- Config Context (stable — only changes on page navigation) ---

export const DEFAULT_CONFIG_VALUES = {
  ctaBtnColor: 'success',
  onFindVinModalOpen: noop,
  onFindVinModalClose: noop,
  onMultipleVinsModalOpen: noop,
  onMultipleVinsModalClose: noop,
  isProjectEveStyle: false,
  params: {},
  isLookUpStyleView: false,
  isBackgroundStyleTabEnabled: false,
  isEmoSearchEnabled: false,
  isSameHeight: true,
  withContainerPadding: true,
  isWIDG1125chal1: false,
  renderAddressTab: false,
  isMobile: false,
  isSellCar: false,
  isSellCarLanding: false,
  isAppraisalLanding: false,
  isSellInCityState: false,
  isVdpEmbedded: false,
  isAppExtensionPage: false,
  creativeId: '',
  location: {},
  referer: undefined,
  isFullWidth: false,
  isVisibleInDrawer: false,
  linksColorClassName: 'text-primary-darker',
  accentColorClassName: 'text-primary-darker',
  accentBgColorClassName: 'bg-primary-opacity-2',
};

export const AppraisalTabsConfigContext = createContext(DEFAULT_CONFIG_VALUES);

// --- State Context (volatile — changes on user interaction) ---

export const DEFAULT_STATE_VALUES = {
  licensePlate: '',
  setLicensePlate: noop,
  stateCode: '',
  setStateCode: noop,
  mmy: {},
  setMmy: noop,
  vin: '',
  setVin: noop,
  queryVin: '',
  isVinEdited: false,
  setIsVinEdited: noop,
  styleIds: [],
  setStyleIds: noop,
  isReadyForRedirect: false,
  setIsReadyForRedirect: noop,
  isAppraiserPageOpened: false,
  vinDecoderStyles: null,
  setVinDecoderStyles: noop,
  vinStyles: null,
  setVinStyles: noop,
  addressInfo: { firstName: '', lastName: '', address: '', apartment: '', city: undefined },
  setAddressInfo: noop,
  wasVinPrePopulated: false,
  setWasVinPrePopulated: noop,
  setColors: noop,
  activeTabId: '',
  setActiveTabId: noop,
  tabs: [],
  setTabs: noop,
  prepopulatedVehicle: undefined,
  setPrepopulatedVehicle: noop,
};

export const AppraisalTabsStateContext = createContext(DEFAULT_STATE_VALUES);

// --- Legacy combined defaults (for backwards compatibility in tests) ---

export const DEFAULT_CONTEXT_VALUES = {
  ...DEFAULT_CONFIG_VALUES,
  ...DEFAULT_STATE_VALUES,
};

// --- Hooks ---

export const useAppraisalTabsConfig = () => useContext(AppraisalTabsConfigContext);

export const useAppraisalTabsState = () => useContext(AppraisalTabsStateContext);

/**
 * @deprecated Use `useAppraisalTabsConfig()` and/or `useAppraisalTabsState()` instead.
 * This convenience hook subscribes to BOTH contexts, so consumers re-render on every
 * state change — defeating the performance benefit of the context split.
 * Will be removed once all v1 consumers are migrated.
 */
export const useAppraisalTabsContext = () => {
  const config = useAppraisalTabsConfig();
  const state = useAppraisalTabsState();
  return useMemo(() => ({ ...config, ...state }), [config, state]);
};

// Legacy export for existing context references
export const AppraisalTabsContext = AppraisalTabsStateContext;

// --- Provider ---

export function AppraisalTabsContextProvider({
  WrappedComponent,
  initialStateCode = '',
  isAppExtensionPage = false,
  isVdpEmbedded = false,
  isLookUpStyleView = false,
  isOpenInNewTab = false,
  location = DEFAULT_CONFIG_VALUES.location,
  ctaBtnColor = 'success',
  onFindVinModalOpen = noop,
  onFindVinModalClose = noop,
  onMultipleVinsModalOpen = noop,
  onMultipleVinsModalClose = noop,
  isProjectEveStyle = false,
  params = DEFAULT_CONFIG_VALUES.params,
  creativeId = '',
  isBackgroundStyleTabEnabled = false,
  isEmoSearchEnabled = false,
  isSameHeight = true,
  withContainerPadding = true,
  isWIDG1125chal1 = false,
  renderAddressTab = false,
  isMobile = false,
  isSellCar = false,
  isSellCarLanding = false,
  isAppraisalLanding = false,
  isSellInCityState = false,
  referer = undefined,
  isFullWidth = false,
  isVisibleInDrawer = false,
  linksColorClassName = 'text-primary-darker',
  accentColorClassName = 'text-primary-darker',
  accentBgColorClassName = 'bg-primary-opacity-2',
  ...restProps
}) {
  const [vin, setVin] = useState('');
  const [isVinEdited, setIsVinEdited] = useState(false);
  const [wasVinPrePopulated, setWasVinPrePopulated] = useState(false);
  const [licensePlate, setLicensePlate] = useState('');
  const [stateCode, setStateCode] = useState(initialStateCode);
  const [mmy, setMmy] = useState({});
  const [addressInfo, setAddressInfo] = useState({
    firstName: '',
    lastName: '',
    address: '',
    apartment: '',
    // IMPORTANT: city should be undefined in order to detect user input.
    city: undefined,
  });
  const [styleIds, setStyleIds] = useState(null); // IMPORTANT: using null here to detect when styleIds are loaded.
  const [isReadyForRedirect, setIsReadyForRedirect] = useState(false);
  const [vinDecoderStyles, setVinDecoderStyles] = useState(null);
  const [vinStyles, setVinStyles] = useState(null);
  const [colors, setColors] = useState({});
  const [isAppraiserPageOpened, setIsAppraiserPageOpened] = useState(false);
  const [activeTabId, setActiveTabId] = useState('');
  const [tabs, setTabs] = useState([]);
  const [prepopulatedVehicle, setPrepopulatedVehicle] = useState(undefined);

  const prevIsReadyForRedirect = usePrevious(isReadyForRedirect);

  const setDefaultButtonStateForCachedPage = useCallback(({ persisted }) => {
    if (persisted) {
      setIsReadyForRedirect(false);
      setVin('');
      setLicensePlate('');
      setMmy({});
    }
  }, []);

  // Reset stale redirect/form state when this page is restored from the browser back-forward cache.
  useEffect(() => {
    window.addEventListener('pageshow', setDefaultButtonStateForCachedPage);

    return () => {
      setIsReadyForRedirect(false);
      window.removeEventListener('pageshow', setDefaultButtonStateForCachedPage);
    };
  }, [setDefaultButtonStateForCachedPage]);

  const { exteriorColor, interiorColor } = colors ?? {};
  const exteriorColorId = exteriorColor?.id;
  const interiorColorId = interiorColor?.id;

  // Redirect once the form state is ready, using the latest vehicle/query values to build the appraisal URL.
  useEffect(() => {
    if (isReadyForRedirect && isReadyForRedirect !== prevIsReadyForRedirect) {
      const { make, model, year } = mmy;
      const { modsId, emo, mileage, condition, promo } = getQuery(location);
      let styleIdsParam;

      if (!isEmpty(vinStyles)) {
        styleIdsParam = vinStyles?.results?.map(style => style?.styleId) ?? [];
      } else {
        styleIdsParam = styleIds;
      }

      const appraisalPathLink = getAppraisalLink({
        make,
        model,
        year,
        queryParams: {
          vin,
          styleIds: styleIdsParam?.join(','),
          modsId,
          mileage,
          condition,
          emo,
          promo,
          colorId: getColorIdForUrl(exteriorColorId),
          interiorColorId: getColorIdForUrl(interiorColorId),
        },
        isAppExtensionPage,
      });

      if (isOpenInNewTab) {
        // Try to open in a NEW tab.
        const openedWindow = window.open(appraisalPathLink, '_blank');
        const isOpenedWithSuccess = !!openedWindow;
        if (isOpenedWithSuccess) {
          // Intentional reset after opening the appraiser page in a new tab.
          // eslint-disable-next-line react-hooks/set-state-in-effect
          setIsReadyForRedirect(false);
          setIsAppraiserPageOpened(true);
        } else {
          // Basically, this means that the tab was BLOCKED as a pop-up.
          EventToolbox.fireTrackAction({
            event_type: TrackingConstant.EVENT_TYPE_ACTION_END,
            event_data: {
              action_name: TrackingConstant.ACTION_APPRAISE_VEHICLE,
              action_category: TrackingConstant.USER_ACTION_CATEGORY,
              action_cause: TrackingConstant.ACTION_CAUSE_LINK_CLICK,
              subaction_name: 'new_tab_open_failure',
              creative_id: creativeId,
              value: 'popup blocker',
            },
          });
          // Open in the SAME tab.
          venomHistory.push(appraisalPathLink);
        }
      } else if (!isLookUpStyleView) {
        venomHistory.push(appraisalPathLink);
      }

      if (vin) {
        checkVinInventory(vin, APPRAISAL_VEHICLE_ENTRY_CREATIVE_ID, noop);
      }
    }
  }, [
    isAppExtensionPage,
    isLookUpStyleView,
    isOpenInNewTab,
    isReadyForRedirect,
    location,
    mmy,
    prevIsReadyForRedirect,
    styleIds,
    vin,
    vinStyles,
    creativeId,
    exteriorColorId,
    interiorColorId,
  ]);

  useEffect(() => {
    if (isAppraiserPageOpened) {
      // Reset this state after it was set to true.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIsAppraiserPageOpened(false);
    }
  }, [isAppraiserPageOpened]);

  const queryVin = get(location, 'query.vin', '').toUpperCase();

  // Config context: stable values derived from props (won't change on user interaction)
  const configProps = useMemo(
    () => ({
      ctaBtnColor,
      onFindVinModalOpen,
      onFindVinModalClose,
      onMultipleVinsModalOpen,
      onMultipleVinsModalClose,
      isProjectEveStyle,
      params,
      isLookUpStyleView,
      isBackgroundStyleTabEnabled,
      isEmoSearchEnabled,
      isSameHeight,
      withContainerPadding,
      isWIDG1125chal1,
      renderAddressTab,
      isMobile,
      isSellCar,
      isSellCarLanding,
      isAppraisalLanding,
      isSellInCityState,
      isVdpEmbedded,
      isAppExtensionPage,
      creativeId,
      location,
      referer,
      isFullWidth,
      isVisibleInDrawer,
      linksColorClassName,
      accentColorClassName,
      accentBgColorClassName,
    }),
    [
      ctaBtnColor,
      onFindVinModalOpen,
      onFindVinModalClose,
      onMultipleVinsModalOpen,
      onMultipleVinsModalClose,
      isProjectEveStyle,
      params,
      isLookUpStyleView,
      isBackgroundStyleTabEnabled,
      isEmoSearchEnabled,
      isSameHeight,
      withContainerPadding,
      isWIDG1125chal1,
      renderAddressTab,
      isMobile,
      isSellCar,
      isSellCarLanding,
      isAppraisalLanding,
      isSellInCityState,
      isVdpEmbedded,
      isAppExtensionPage,
      creativeId,
      location,
      referer,
      isFullWidth,
      isVisibleInDrawer,
      linksColorClassName,
      accentColorClassName,
      accentBgColorClassName,
    ]
  );

  // State context: volatile values that change on user interaction
  const stateProps = useMemo(
    () => ({
      licensePlate,
      setLicensePlate,
      mmy,
      setMmy,
      vin,
      setVin,
      isVinEdited,
      setIsVinEdited,
      queryVin,
      stateCode,
      setStateCode,
      styleIds,
      setStyleIds,
      vinDecoderStyles,
      setVinDecoderStyles,
      vinStyles,
      setVinStyles,
      addressInfo,
      setAddressInfo,
      isReadyForRedirect,
      setIsReadyForRedirect,
      isAppraiserPageOpened,
      wasVinPrePopulated,
      setWasVinPrePopulated,
      // Only the setter is exposed — colors are consumed internally by the redirect
      // effect (exteriorColorId/interiorColorId). Children push color selections up
      // via setColors; they never need to read the current value back.
      setColors,
      activeTabId,
      setActiveTabId,
      tabs,
      setTabs,
      prepopulatedVehicle,
      setPrepopulatedVehicle,
    }),
    [
      licensePlate,
      mmy,
      vin,
      isVinEdited,
      queryVin,
      stateCode,
      styleIds,
      vinDecoderStyles,
      vinStyles,
      addressInfo,
      isReadyForRedirect,
      isAppraiserPageOpened,
      wasVinPrePopulated,
      activeTabId,
      tabs,
      prepopulatedVehicle,
    ]
  );

  return (
    <AppraisalTabsConfigContext.Provider value={configProps}>
      <AppraisalTabsStateContext.Provider value={stateProps}>
        {/* vin is passed as a prop because connectToModel's bindToPath reads it
            from props to build the MultiOfferModel path. Setters are available in
            AppraisalTabsStateContext — consumers should read them from there. */}
        <WrappedComponent
          {...restProps}
          initialStateCode={initialStateCode}
          isAppExtensionPage={isAppExtensionPage}
          isVdpEmbedded={isVdpEmbedded}
          isLookUpStyleView={isLookUpStyleView}
          isOpenInNewTab={isOpenInNewTab}
          location={location}
          ctaBtnColor={ctaBtnColor}
          onFindVinModalOpen={onFindVinModalOpen}
          onFindVinModalClose={onFindVinModalClose}
          onMultipleVinsModalOpen={onMultipleVinsModalOpen}
          onMultipleVinsModalClose={onMultipleVinsModalClose}
          isProjectEveStyle={isProjectEveStyle}
          params={params}
          creativeId={creativeId}
          isBackgroundStyleTabEnabled={isBackgroundStyleTabEnabled}
          isEmoSearchEnabled={isEmoSearchEnabled}
          isSameHeight={isSameHeight}
          withContainerPadding={withContainerPadding}
          isWIDG1125chal1={isWIDG1125chal1}
          renderAddressTab={renderAddressTab}
          isMobile={isMobile}
          isSellCar={isSellCar}
          isSellCarLanding={isSellCarLanding}
          isAppraisalLanding={isAppraisalLanding}
          isSellInCityState={isSellInCityState}
          referer={referer}
          isFullWidth={isFullWidth}
          isVisibleInDrawer={isVisibleInDrawer}
          linksColorClassName={linksColorClassName}
          accentColorClassName={accentColorClassName}
          accentBgColorClassName={accentBgColorClassName}
          vin={vin}
        />
      </AppraisalTabsStateContext.Provider>
    </AppraisalTabsConfigContext.Provider>
  );
}

AppraisalTabsContextProvider.propTypes = {
  WrappedComponent: PropTypes.elementType.isRequired,
  initialStateCode: PropTypes.string,
  isAppExtensionPage: PropTypes.bool,
  isVdpEmbedded: PropTypes.bool,
  isLookUpStyleView: PropTypes.bool,
  isOpenInNewTab: PropTypes.bool,
  location: PropTypes.shape({}),
  ctaBtnColor: PropTypes.string,
  onFindVinModalOpen: PropTypes.func,
  onFindVinModalClose: PropTypes.func,
  onMultipleVinsModalOpen: PropTypes.func,
  onMultipleVinsModalClose: PropTypes.func,
  isProjectEveStyle: PropTypes.bool,
  params: PropTypes.shape({
    make: PropTypes.string,
    model: PropTypes.string,
    year: PropTypes.string,
    styleId: PropTypes.string,
  }),
  creativeId: PropTypes.string,
  isBackgroundStyleTabEnabled: PropTypes.bool,
  isEmoSearchEnabled: PropTypes.bool,
  isSameHeight: PropTypes.bool,
  withContainerPadding: PropTypes.bool,
  isWIDG1125chal1: PropTypes.bool,
  renderAddressTab: PropTypes.bool,
  isMobile: PropTypes.bool,
  isSellCar: PropTypes.bool,
  isSellCarLanding: PropTypes.bool,
  isAppraisalLanding: PropTypes.bool,
  isSellInCityState: PropTypes.bool,
  referer: PropTypes.string,
  isFullWidth: PropTypes.bool,
  isVisibleInDrawer: PropTypes.bool,
  linksColorClassName: PropTypes.string,
  accentColorClassName: PropTypes.string,
  accentBgColorClassName: PropTypes.string,
};
